import { Alert } from '@/components/ui/alert';
import { buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { VERTRAG_MAX_MITARBEITER } from '@/domain/vertraege/features/VertragCarousel/UpgradeVertragCard/constants';
import { VertragUpgraden } from '@/domain/vertraege/features/VertragUpgraden/VertragUpgrade';
import { Vertrag } from '@/domain/vertraege/shared/types/vertrag';
import { cn } from '@/lib/utils';
import {
  faArrowUpRight,
  faCalendarDays,
  faCircleExclamation,
  faFileCheck,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

// eslint-disable-next-line max-lines-per-function
export function UpgradeVertragCard({ vertrag }: { vertrag: Vertrag }) {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);

  const [anzahlMitarbeiter, setAnzahlMitarbeiter] = useState(
    vertrag.aktiveAnzahl,
  );
  const [kontingent, setKontingent] = useState(vertrag.vereinbarteAnzahl);
  const [preis, setPreis] = useState(calculatePreis(kontingent));

  function calculatePreis(kontingent: number): string {
    const newPreis = 50 + (kontingent / 5) * 25;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(newPreis);
  }

  function handleUpdateAnzahlMitarbeiter(value: number): void {
    const anzahlMitarbeiter = value;
    setAnzahlMitarbeiter(anzahlMitarbeiter);

    if (value <= VERTRAG_MAX_MITARBEITER) {
      const newKontingent = Math.ceil(anzahlMitarbeiter / 5) * 5;
      setKontingent(newKontingent);
      setPreis(calculatePreis(newKontingent));
    }
  }

  return (
    <div className="h-full relative w-[600px]">
      <div className="absolute -top-2 left-1/2 z-10 -translate-x-1/2">
        <div className="bg-secondary-indigo-500 text-white text-xs rounded-full py-0.5 px-1">
          {t('domain.vertraege.upgradeInfo.badge')}
        </div>
      </div>

      <Card className="h-full relative overflow-hidden border-transparent bg-white p-5 text-neutrals-900 flex flex-col gap-5 shadow-none">
        <div className="absolute p-0.5 inset-0 rounded-md bg-linear-to-r from-primary-blue-600 via-secondary-indigo-500 to-secondary-purple-500 opacity-100">
          <div className="h-full w-full rounded-md bg-white" />
        </div>
        <div className="relative h-full z-10 flex flex-col justify-between gap-5">
          <div className="flex flex-col gap-2">
            <div className="font-bold">
              {t('domain.vertraege.upgradeInfo.title')}
            </div>
            <div className="text-sm">
              {t('domain.vertraege.upgradeInfo.subtitle')}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="font-semibold">
              {t('domain.vertraege.upgradeInfo.list.title')}
            </div>
            <div className="flex gap-2 items-center">
              <FontAwesomeIcon icon={faCalendarDays} />
              <a
                href="https://e2n.de/assistent/"
                target="_blank"
                rel="noreferrer"
                className="text-sm">
                {t('domain.vertraege.upgradeInfo.list.e2nAssistent')}
                <FontAwesomeIcon
                  className="text-primary-blue-600"
                  icon={faArrowUpRight}
                />
              </a>
            </div>
            <div className="flex gap-2 items-center">
              <FontAwesomeIcon icon={faFileCheck} />
              <a
                className="text-sm"
                href="https://e2n.de/elektronische-arbeitsunfaehigkeitsbescheinigung/"
                rel="noreferrer"
                target="_blank">
                {t('domain.vertraege.upgradeInfo.list.eAU')}{' '}
                <FontAwesomeIcon
                  className="text-primary-blue-600"
                  icon={faArrowUpRight}
                />
              </a>
            </div>
          </div>
          <hr className="border-t border-neutrals-300" />
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="text-sm">
                {t('domain.vertraege.upgradeInfo.anzahlMitarbeiter')}
              </div>
              <Input
                className="w-24"
                value={anzahlMitarbeiter}
                endIcon={faUserGroup}
                onChange={(event) => {
                  const value = parseInt(event.target.value, 10);
                  if (!isNaN(value)) {
                    setAnzahlMitarbeiter(value);
                  } else {
                    setAnzahlMitarbeiter(0);
                  }
                }}
                onBlur={(event) => {
                  const value = parseInt(event.target.value, 10);
                  if (!isNaN(value)) {
                    handleUpdateAnzahlMitarbeiter(value);
                  } else {
                    handleUpdateAnzahlMitarbeiter(0);
                  }
                }}
              />
            </div>
            <Slider
              value={[anzahlMitarbeiter]}
              max={VERTRAG_MAX_MITARBEITER}
              step={1}
              className="w-full"
              onValueChange={(value) => handleUpdateAnzahlMitarbeiter(value[0])}
            />
            <div className="text-sm">
              {t('domain.vertraege.upgradeInfo.kontigent')}{' '}
              <span className="font-semibold">
                {kontingent} {t('domain.vertraege.slide.employeeTitle')}
              </span>
            </div>
            {anzahlMitarbeiter > VERTRAG_MAX_MITARBEITER && (
              <div className="flex items-center gap-2 text-primary-red-400 text-sm">
                <FontAwesomeIcon icon={faCircleExclamation} />
                <p>
                  <Trans i18nKey="domain.vertraege.upgradeInfo.zuVieleMitarbeiterInfo">
                    Wenn Du mehr als 500 Mitarbeitende hast, kontaktiere uns
                    einfach unter:
                    <a href="tel:+499317304400">+49 931 / 730 440-0</a>
                    oder <a href="mailto:sales@e2n.de">sales@e2n.de</a>
                  </Trans>
                </p>
              </div>
            )}
            {anzahlMitarbeiter <= vertrag.aktiveAnzahl && (
              <div className="flex items-center gap-2 text-primary-red-400 text-sm">
                <FontAwesomeIcon icon={faCircleExclamation} />
                <p>{t('domain.vertraege.upgradeInfo.kontingentReichtNicht')}</p>
              </div>
            )}
            <div className="flex flex-col items-end justify-end w-full">
              <div className="text-xs text-neutrals-700">
                {t('domain.vertraege.slide.taxInfo')}
              </div>
              <span className="text-xs text-neutrals-700">
                {t('domain.vertraege.slide.tax')}
              </span>
              <div className="flex pb-2 justify-end font-bold gap-2">
                <span className="flex top-0 -mt-1 text-xl align-top font-semibold">
                  {t('general.currency.EUR')}
                </span>
                <span className="flex text-2xl">{preis}</span>
              </div>
              <div className="text-xs text-neutrals-700 uppercase">
                {t('domain.vertraege.upgradeInfo.überweisung')}
              </div>
            </div>
          </div>
          {showSuccess && (
            <Alert className="text-sm" variant="info">
              <Trans i18nKey="domain.vertraege.upgradeForm.submit.successDescriptionCard" />
            </Alert>
          )}
          <div className="flex items-center gap-4">
            <VertragUpgraden
              onSuccess={() => setShowSuccess(true)}
              vertrag={vertrag}
              preis={preis}
              anzahlMitarbeiter={anzahlMitarbeiter}
              kontingent={kontingent}
            />
            <a
              className={cn(
                buttonVariants({ variant: 'ghost' }),
                'text-primary-blue-600',
              )}
              href="https://meetings.hubspot.com/kambobe/beratungene2nassistent"
              target="_blank"
              rel="noreferrer">
              {t('domain.vertraege.upgradeInfo.actions.terminVereinbaren')}
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
}
